import {
  ExternalImageUrlParams,
  FedopsInteraction,
  UploadFileFn,
  SectionPresetDefinition,
} from '../types';

export interface ImageGenerator {
  getImages: (params: GetImagesParams) => Promise<ImagesMap | undefined>;
}

export interface ImageGeneratorOptions {
  interactionStarted?: FedopsInteraction;
  interactionEnded?: FedopsInteraction;
  isAiTextOnImageOpen?: boolean;
  featureName?: string;
}

export type GetImagesParams = {
  sectionName: string;
  imagesMap: ImagesMap;
  imageGenerationParams: ImageGenerationParams;
  imageUploadHandlers: ImageUploadHandlers;
};

export type AspectRatio =
  | '1:1'
  | '2:3'
  | '3:2'
  | '4:5'
  | '5:4'
  | '9:16'
  | '16:9'
  | '9:21'
  | '21:9';

export interface ImageResolution {
  height?: number;
  width?: number;
}

export interface ImageDetails extends ImageResolution {
  fieldRole?: string;
  localUri?: string;
  staticUri?: string;
  displayName?: string;
  promptKey?: string;
  aspectRatio?: AspectRatio;
  allowedFlows?: TEXT_ON_IMAGE_FLOWS[];
}

// This type alias is temporar
// Please change to a proper interface when support of imageDescription will be added
export type ImageDescription = object;

export type ImagesMap = Map<string, ImageDetails>;

export type PromptsData = Record<string, string>;

export enum DefaultSectionName {
  WELCOME = 'Welcome',
  ABOUT = 'About',
  GALLERY = 'Gallery',
  INSTAGRAM = 'Instagram',
  GENERAL = 'General',
  TEAM = 'Team',
  TESTIMONIALS = 'Testimonials',
}

export enum AbstractSectionName {
  ABSTRACT = 'Abstract',
}

export enum FIELD_ROLES {
  CROPTYPE_PORTRAIT = 'CROPTYPE_PORTRAIT',
  CROPTYPE_LANDSCAPE = 'CROPTYPE_LANDSCAPE',
  CROPTYPE_ABSTRACT = 'CROPTYPE_ABSTRACT',
  ABSTRACT = 'abstract',
}

export type SectionName = DefaultSectionName | AbstractSectionName;

export interface EventData<Type = string, Payload = { [key: string]: string }> {
  type: Type;
  payload: Payload;
}

export type GenerationEventData =
  | EventData<'onBunchGenerationStart', { amount: string; bunch: string }>
  | EventData<'onBunchGenerationSuccess', { amount: string; bunch: string }>
  | EventData<'onSingleGenerationStart', { id: string; bunch: string }>
  | EventData<
      'onSingleGenerationSuccess',
      { id: string; bunch: string; details: string }
    >
  | EventData<
      'onSingleGenerationError',
      { id: string; bunch: string; errn: string; details?: string }
    >
  | EventData<
      'onTextOnImageGenerationEnd',
      {
        flow: string;
        photoScene?: string;
        imageUrl?: string;
        controlImageUrl?: string;
        details: string;
      }
    >;

export type GenerationEventHandler = (eventData: GenerationEventData) => void;

export type UploadEventData =
  | EventData<'onBunchUploadStart', { amount: string; bunch: string }>
  | EventData<'onBunchUploadSuccess', { amount: string; bunch: string }>
  | EventData<'onSingleUploadStart', { bunch: string; compId: string }>
  | EventData<
      'onSingleUploadSuccess',
      { bunch: string; compId: string; imageUrl: string }
    >;

export type UploadEventHandler = (eventData: UploadEventData) => void;

export type ImageUploadHandlers = {
  imageUrlUploadFn?: ({
    url,
    name,
  }: ExternalImageUrlParams) => Promise<string> | undefined;
  fileUploadFn?: UploadFileFn;
};

export interface ImageGenerationParams {
  msId: string;
  msInstance: string;
  businessTerm: string;
  sectionId?: string;
  businessTermId?: string;
  siteDescription?: string;
  onGenerationEvent: GenerationEventHandler;
}

export interface GetImageParams {
  msInstance: string;
  promptKey: string;
  imageResolution: ImageResolution;
  aspectRatio: string;
  compId: string;
  isAiImageNSFWBlacklisted: boolean;
}

export type UriData = Map<string, string>;

export type UploadImagesDetails = [compId: string, imageDetails: ImageDetails];

export interface TextOnImageDefaultParams {
  photoTheme: string;
  businessTerm: string;
  siteDescription: string;
  sectionId?: string;
}

export interface TextOnImageGetFlowParams extends TextOnImageDefaultParams {
  allowedStyles: string;
}

export interface TextOnImageGetPhotoSceneParams
  extends TextOnImageDefaultParams {
  brightness: TEXT_ON_IMAGE_BRIGHTNESS;
}

interface TextOnImagePhotoBaseParams {
  brightness: TEXT_ON_IMAGE_BRIGHTNESS;
}

export interface TextOnImageSkyPhotoParams extends TextOnImagePhotoBaseParams {
  controlImageUrl: string;
  sky_item: string;
}

export interface TextOnImagePortraitPhotoParams
  extends TextOnImagePhotoBaseParams {
  controlImage: string;
  portrait_item1: string;
}

export interface TextOnImageNoPointPhotoParams {
  no_point1: string;
  brightness: TEXT_ON_IMAGE_BRIGHTNESS;
  seedImage: string;
}
export interface TextOnImageObjectPhotoParams {
  object_item1: string;
  brightness: TEXT_ON_IMAGE_BRIGHTNESS;
  seedImage: string;
}

export enum TEXT_ON_IMAGE_FLOWS {
  SKY = 'sky_Flow',
  PORTRAIT = 'portrait_Flow',
  OBJECT = 'object_Flow',
  DATA_VISUALIZATION = 'data_Visualization_Flow',
  NO_POINT_OF_INTEREST = 'no_Point_of_Interest_Flow',
}

export enum TEXT_ON_IMAGE_BRIGHTNESS {
  BRIGHT = 'bright',
  DARK = 'dark',
}

export interface TextOnImageObjectDimensions {
  pxLeft: number;
  pxTop: number;
  height: number;
  width: number;
}

export interface TextOnImagePhotosDefinition {
  _id: string;
  url: string;
  title: string;
  sections: SectionPresetDefinition[];
  dimensions?: TextOnImageObjectDimensions;
  brightness?: TEXT_ON_IMAGE_BRIGHTNESS;
}

export interface TextOnImageFlowsDefinition {
  _id: string;
  title: string;
  sectionName: string;
  flow: string;
  images: TextOnImagePhotosDefinition[];
}

export interface TextOnImageResponse {
  imageUrl?: string;
  photoScene: string;
  controlImageUrl: string;
}
